import { useCallback } from 'react';

import type { ActorListEnum, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { CustomizeMutation } from '../types/props.type';
import type { SubmitHandler } from '../types/submit-handler.type';

interface Params {
  readonly folderBusinessEntityId: Id;
  readonly list: ActorListEnum;
  readonly directMutation?: CustomizeMutation;
  readonly handleCustomizeSuccess: () => void;
}

interface FormikArrayActions {
  readonly customizeInForm: () => void;
}

export const useCustomizeFolderBusinessEntityActorsSubmit = ({
  folderBusinessEntityId,
  list,
  directMutation,
  handleCustomizeSuccess,
}: Readonly<Params>) => {
  const handleSubmitFactory = useCallback(
    ({ customizeInForm }: Readonly<FormikArrayActions>): SubmitHandler =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const onSuccess = () => {
          customizeInForm();

          handleCustomizeSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              actorableId: folderBusinessEntityId,
              list,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [folderBusinessEntityId, directMutation, handleCustomizeSuccess, list],
  );

  return { handleSubmitFactory };
};
